import { useEffect, useMemo, useRef, useState } from "react";
import { SIMULATION_CONFIG } from "../config/simulationConfig";
import { buildWheelEntries, createSeededRandom, selectWheelWinnerFromEntries } from "../utils/wheelSimulation";
import { buildSimulationReport } from "../utils/simulationStats";

const clampSpins = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return SIMULATION_CONFIG.defaultSpins;
  return Math.min(
    Math.max(Math.floor(parsed), SIMULATION_CONFIG.minSpins),
    SIMULATION_CONFIG.maxSpins,
  );
};

const formatPercent = (value) => `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;

const getBarColor = (row) => {
  if (row.outsideConfidenceInterval) return "#f97316";
  if (Math.abs(row.percentageDeviation) >= 5) return "#f59e0b";
  return "#38bdf8";
};

const formatLabel = (entry) => {
  if (!entry) return "";
  return entry.count > 1 ? `${entry.name} x${entry.count}` : entry.name;
};

function SimulationTestWindow({ visible, onClose, names, settings }) {
  const [requestedSpins, setRequestedSpins] = useState(SIMULATION_CONFIG.defaultSpins);
  const [seedInput, setSeedInput] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [completedSpins, setCompletedSpins] = useState(0);
  const [counts, setCounts] = useState([]);
  const [report, setReport] = useState(null);
  const [statusMessage, setStatusMessage] = useState("Choose a spin count and start the simulation.");
  const stopRequestedRef = useRef(false);
  const animationFrameRef = useRef(0);
  const runTokenRef = useRef(0);

  const wheelEntries = useMemo(
    () => buildWheelEntries(names, Boolean(settings?.clusterNames)),
    [names, settings?.clusterNames],
  );

  const chartRows = useMemo(() => {
    return wheelEntries.map((entry, index) => {
      const row = report?.rows?.find((candidate) => candidate.index === index) || null;
      return {
        index,
        label: formatLabel(entry),
        count: counts[index] || 0,
        row,
      };
    });
  }, [counts, report, wheelEntries]);

  const maxChartCount = useMemo(
    () => Math.max(1, ...chartRows.map((row) => row.count || 0)),
    [chartRows],
  );

  useEffect(() => {
    if (!visible) {
      stopRequestedRef.current = true;
      setIsRunning(false);
      return;
    }

    setRequestedSpins((current) => clampSpins(current));
    setCounts(new Array(wheelEntries.length).fill(0));
    setCompletedSpins(0);
    setReport(null);
    setStatusMessage("Choose a spin count and start the simulation.");
  }, [visible, wheelEntries.length]);

  useEffect(() => {
    return () => {
      stopRequestedRef.current = true;
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  useEffect(() => {
    if (!isRunning) return undefined;

    if (!wheelEntries.length) {
      setStatusMessage("Add names to run a simulation.");
      setIsRunning(false);
      return undefined;
    }

    stopRequestedRef.current = false;
    const runToken = runTokenRef.current + 1;
    runTokenRef.current = runToken;
    const totalSpins = clampSpins(requestedSpins);
    const random = createSeededRandom(seedInput.trim() || undefined);
    const nextCounts = new Array(wheelEntries.length).fill(0);
    let completed = 0;
    const batchSize = Math.max(
      100,
      Math.min(SIMULATION_CONFIG.batchSize, Math.max(1, Math.floor(totalSpins / 200))),
    );

    setStatusMessage("Running simulation...");
    setCompletedSpins(0);
    setCounts(nextCounts);
    setReport(null);

    const pushSnapshot = () => {
      const nextReport = buildSimulationReport({
        labels: wheelEntries.map((entry) => formatLabel(entry)),
        counts: nextCounts,
        totalSpins,
      });
      setCounts([...nextCounts]);
      setCompletedSpins(completed);
      setReport(nextReport);
    };

    const step = () => {
      if (stopRequestedRef.current || runTokenRef.current !== runToken) {
        setStatusMessage(`Stopped at ${completed.toLocaleString()} spins.`);
        setIsRunning(false);
        pushSnapshot();
        return;
      }

      const target = Math.min(completed + batchSize, totalSpins);
      while (completed < target) {
        const result = selectWheelWinnerFromEntries({
          names,
          wheelEntries,
          random,
        });

        if (result && typeof result.winnerEntryIndex === "number") {
          nextCounts[result.winnerEntryIndex] += 1;
        }

        completed += 1;
      }

      pushSnapshot();

      if (completed < totalSpins) {
        animationFrameRef.current = requestAnimationFrame(step);
        return;
      }

      setStatusMessage(`Completed ${totalSpins.toLocaleString()} spins.`);
      setIsRunning(false);
    };

    animationFrameRef.current = requestAnimationFrame(step);

    return () => {
      stopRequestedRef.current = true;
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, [isRunning, names, requestedSpins, seedInput, wheelEntries]);

  const handleStartStop = () => {
    if (isRunning) {
      stopRequestedRef.current = true;
      setIsRunning(false);
      return;
    }

    const clamped = clampSpins(requestedSpins);
    setRequestedSpins(clamped);
    setCompletedSpins(0);
    setCounts(new Array(wheelEntries.length).fill(0));
    setReport(null);
    setStatusMessage("");
    setIsRunning(true);
  };

  const canRun = wheelEntries.length > 0 && requestedSpins >= SIMULATION_CONFIG.minSpins;

  if (!visible) return null;

  return (
    <div style={styles.overlay} onClick={isRunning ? undefined : onClose}>
      <div style={styles.modal} onClick={(event) => event.stopPropagation()}>
        <div style={styles.headerRow}>
          <div>
            <h2 style={styles.title}>Simulation Test</h2>
            <p style={styles.subtitle}>
              Run fast wheel trials with the production winner logic and watch the histogram update live.
            </p>
          </div>
          <button
            type="button"
            style={styles.closeButton}
            onClick={isRunning ? handleStartStop : onClose}
            aria-label={isRunning ? "Stop simulation" : "Close simulation test"}
          >
            {isRunning ? "Stop" : "×"}
          </button>
        </div>

        <div style={styles.controls}>
          <label style={styles.field}>
            <span style={styles.fieldLabel}>Spin count</span>
            <input
              type="number"
              min={SIMULATION_CONFIG.minSpins}
              max={SIMULATION_CONFIG.maxSpins}
              step={1}
              value={requestedSpins}
              onChange={(event) => setRequestedSpins(clampSpins(event.target.value))}
              disabled={isRunning}
              style={styles.numberInput}
            />
          </label>

          <div style={styles.presetRow}>
            {SIMULATION_CONFIG.presets.map((preset) => (
              <button
                key={preset}
                type="button"
                className="names-btn secondary"
                style={styles.presetButton}
                onClick={() => setRequestedSpins(preset)}
                disabled={isRunning}
              >
                {preset.toLocaleString()}
              </button>
            ))}
          </div>

          <label style={styles.field}>
            <span style={styles.fieldLabel}>Optional random seed</span>
            <input
              type="text"
              placeholder="Leave blank for Math.random"
              value={seedInput}
              onChange={(event) => setSeedInput(event.target.value)}
              disabled={isRunning}
              style={styles.textInput}
            />
          </label>

          <div style={styles.actionRow}>
            <button
              type="button"
              className="names-btn secondary"
              onClick={handleStartStop}
              disabled={!canRun && !isRunning}
              style={styles.startButton}
            >
              {isRunning ? "Stop" : "Start"}
            </button>
            <div style={styles.statusText}>{statusMessage || "Ready to run."}</div>
          </div>
        </div>

        <div style={styles.summaryStrip}>
          <span>Spins: {completedSpins.toLocaleString()} / {requestedSpins.toLocaleString()}</span>
          <span>Segments: {wheelEntries.length}</span>
          {report ? <span>p-value: {report.pValue.toFixed(6)}</span> : null}
          {report ? <span>{report.passesAlpha ? "No bias detected" : "Potential bias detected"}</span> : null}
        </div>

        <div style={styles.chartPanel}>
          <div style={styles.yAxis}>
            <span>{maxChartCount.toLocaleString()}</span>
            <span>{Math.ceil(maxChartCount / 2).toLocaleString()}</span>
            <span>0</span>
          </div>
          <div style={styles.chartScroll}>
            <div style={styles.chart} role="img" aria-label="Live histogram of simulation results">
              {chartRows.map((row) => {
                const height = `${Math.max(4, (row.count / maxChartCount) * 100)}%`;
                const barColor = row.row ? getBarColor(row.row) : "#38bdf8";

                return (
                  <div key={row.index} style={styles.chartColumn}>
                    <div style={styles.barWrap}>
                      <div
                        style={{
                          ...styles.bar,
                          height,
                          background: barColor,
                        }}
                        title={`${row.label}: ${row.count.toLocaleString()} wins`}
                      />
                    </div>
                    <div style={styles.barCount}>{row.count.toLocaleString()}</div>
                    <div style={styles.barLabel}>{row.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {report ? (
          <div style={styles.resultsPanel}>
            <div style={styles.resultsGrid}>
              <div style={styles.resultCard}><strong>Chi-square</strong><span>{report.chiSquare.toFixed(6)}</span></div>
              <div style={styles.resultCard}><strong>Degrees of freedom</strong><span>{report.degreesOfFreedom}</span></div>
              <div style={styles.resultCard}><strong>P-value</strong><span>{report.pValue.toFixed(6)}</span></div>
              <div style={styles.resultCard}><strong>Expected count</strong><span>{report.expectedCount.toFixed(3)}</span></div>
              <div style={styles.resultCard}><strong>Largest positive deviation</strong><span>{report.largestPositiveDeviation ? `${report.largestPositiveDeviation.label} (${formatPercent(report.largestPositiveDeviation.percentageDeviation)})` : "-"}</span></div>
              <div style={styles.resultCard}><strong>Largest negative deviation</strong><span>{report.largestNegativeDeviation ? `${report.largestNegativeDeviation.label} (${formatPercent(report.largestNegativeDeviation.percentageDeviation)})` : "-"}</span></div>
              <div style={styles.resultCard}><strong>Max abs. deviation</strong><span>{formatPercent(report.maxAbsolutePercentageError)}</span></div>
              <div style={styles.resultCard}><strong>Std. dev. of counts</strong><span>{report.standardDeviationCounts.toFixed(6)}</span></div>
            </div>

            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.tableHead}>Name</th>
                    <th style={styles.tableHead}>Count</th>
                    <th style={styles.tableHead}>Observed</th>
                    <th style={styles.tableHead}>Expected</th>
                    <th style={styles.tableHead}>Deviation</th>
                    <th style={styles.tableHead}>% Dev.</th>
                    <th style={styles.tableHead}>95% CI</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((row) => (
                    <tr key={`${row.index}-${row.label}`} style={row.outsideConfidenceInterval ? styles.warningRow : undefined}>
                      <td style={styles.tableCell}>{row.label}</td>
                      <td style={styles.tableCell}>{row.count.toLocaleString()}</td>
                      <td style={styles.tableCell}>{(row.observedProbability * 100).toFixed(4)}%</td>
                      <td style={styles.tableCell}>{(row.expectedProbability * 100).toFixed(4)}%</td>
                      <td style={styles.tableCell}>{row.countDifference.toFixed(2)}</td>
                      <td style={styles.tableCell}>{formatPercent(row.percentageDeviation)}</td>
                      <td style={styles.tableCell}>
                        {(row.confidenceIntervalLow * 100).toFixed(4)}% - {(row.confidenceIntervalHigh * 100).toFixed(4)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(4, 10, 18, 0.72)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    padding: 20,
    backdropFilter: "blur(8px)",
  },
  modal: {
    width: "min(1280px, 96vw)",
    maxHeight: "92vh",
    overflow: "auto",
    borderRadius: 18,
    background: "linear-gradient(180deg, rgba(19, 30, 42, 0.98), rgba(12, 18, 28, 0.98))",
    border: "1px solid rgba(148, 163, 184, 0.18)",
    boxShadow: "0 30px 90px rgba(0, 0, 0, 0.55)",
    color: "#e2e8f0",
    padding: 24,
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 20,
    marginBottom: 20,
  },
  title: {
    margin: 0,
    fontSize: 28,
    lineHeight: 1.1,
    color: "#fff",
  },
  subtitle: {
    margin: "8px 0 0",
    maxWidth: 720,
    color: "#94a3b8",
    fontSize: 14,
  },
  closeButton: {
    minWidth: 42,
    height: 42,
    borderRadius: 999,
    border: "1px solid rgba(148, 163, 184, 0.3)",
    background: "rgba(15, 23, 42, 0.75)",
    color: "#fff",
    fontSize: 20,
    cursor: "pointer",
  },
  controls: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 260px) minmax(260px, 1fr) minmax(260px, 1fr)",
    gap: 16,
    alignItems: "end",
    marginBottom: 18,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  fieldLabel: {
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: "#94a3b8",
  },
  numberInput: {
    height: 44,
    borderRadius: 12,
    border: "1px solid rgba(148, 163, 184, 0.25)",
    background: "rgba(15, 23, 42, 0.85)",
    color: "#e2e8f0",
    padding: "0 14px",
    fontSize: 15,
  },
  textInput: {
    height: 44,
    borderRadius: 12,
    border: "1px solid rgba(148, 163, 184, 0.25)",
    background: "rgba(15, 23, 42, 0.85)",
    color: "#e2e8f0",
    padding: "0 14px",
    fontSize: 15,
  },
  presetRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
  presetButton: {
    minWidth: 102,
  },
  actionRow: {
    display: "flex",
    alignItems: "center",
    gap: 14,
  },
  startButton: {
    minWidth: 120,
  },
  statusText: {
    color: "#cbd5e1",
    fontSize: 14,
  },
  summaryStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    alignItems: "center",
    padding: "12px 14px",
    borderRadius: 14,
    background: "rgba(15, 23, 42, 0.5)",
    border: "1px solid rgba(148, 163, 184, 0.14)",
    marginBottom: 18,
    color: "#cbd5e1",
    fontSize: 14,
  },
  chartPanel: {
    display: "grid",
    gridTemplateColumns: "56px minmax(0, 1fr)",
    gap: 14,
    alignItems: "stretch",
    padding: 16,
    borderRadius: 16,
    background: "rgba(15, 23, 42, 0.45)",
    border: "1px solid rgba(148, 163, 184, 0.12)",
    marginBottom: 18,
  },
  yAxis: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    color: "#94a3b8",
    fontSize: 12,
    padding: "6px 0",
    minHeight: 260,
  },
  chartScroll: {
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: 8,
  },
  chart: {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "minmax(68px, 1fr)",
    gap: 10,
    alignItems: "end",
    minHeight: 260,
    minWidth: "100%",
  },
  chartColumn: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    gap: 8,
    minHeight: 260,
  },
  barWrap: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    height: 190,
    minHeight: 190,
    borderBottom: "1px solid rgba(148, 163, 184, 0.15)",
    paddingBottom: 6,
  },
  bar: {
    width: "100%",
    maxWidth: 42,
    minHeight: 4,
    borderRadius: "10px 10px 4px 4px",
    boxShadow: "0 0 0 1px rgba(255,255,255,0.04) inset",
    transition: "height 120ms linear",
  },
  barCount: {
    textAlign: "center",
    color: "#e2e8f0",
    fontSize: 12,
    minHeight: 16,
  },
  barLabel: {
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 11,
    lineHeight: 1.2,
    wordBreak: "break-word",
  },
  resultsPanel: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  resultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
  },
  resultCard: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    padding: 14,
    borderRadius: 14,
    background: "rgba(15, 23, 42, 0.5)",
    border: "1px solid rgba(148, 163, 184, 0.14)",
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 14,
    border: "1px solid rgba(148, 163, 184, 0.14)",
    background: "rgba(15, 23, 42, 0.45)",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 960,
  },
  tableHead: {
    padding: "12px 14px",
    textAlign: "left",
    fontSize: 12,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "#94a3b8",
    borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
  },
  tableCell: {
    padding: "11px 14px",
    borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
    fontSize: 13,
    color: "#e2e8f0",
    verticalAlign: "top",
  },
  warningRow: {
    background: "rgba(249, 115, 22, 0.12)",
  },
};

export default SimulationTestWindow;